class ScentProfilesScroller extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isLocked = false;
    this.isAutoScrolling = false;
    this.hasEnteredViewport = false;

    // Scroll speed multiplier: higher = slower scroll (more distance to cover per profile)
    // 1 = normal (100vh per profile), 2 = 2x slower (200vh per profile), etc.
    this.scrollSpeedMultiplier = 1;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.profiles = Array.from(this.querySelectorAll('[data-scent-profile]'));
    this.dots = Array.from(this.querySelectorAll('[data-pagination-dot]'));
    this.totalProfiles = this.profiles.length;

    if (this.totalProfiles <= 1) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setContainerHeight();
    this.bindEvents(signal);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setContainerHeight() {
    // Each profile takes scrollSpeedMultiplier * 100vh of scroll distance
    this.style.height = `${this.totalProfiles * 100 * this.scrollSpeedMultiplier}vh`;
  }

  bindEvents(signal) {
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10);
        this.goToProfile(index);
      }, { signal });
    });

    let lastScrollY = window.scrollY;
    let scrollDirection = 0;

    const handleScroll = () => {
      if (this.isAutoScrolling) return;

      const rect = this.getBoundingClientRect();
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const viewportHeight = window.innerHeight;

      scrollDirection = window.scrollY > lastScrollY ? 1 : -1;
      lastScrollY = window.scrollY;

      // Track when section enters viewport (without forcing scroll)
      if (!this.hasEnteredViewport && scrollDirection === 1) {
        const visibleHeight = Math.min(viewportHeight, viewportHeight - containerTop);
        const visibilityRatio = visibleHeight / viewportHeight;

        if (visibilityRatio >= 0.5 && containerTop > 0) {
          this.hasEnteredViewport = true;
        }
      }

      // Reset hasEnteredViewport when section is out of view
      if (containerTop > viewportHeight || containerTop + containerHeight < 0) {
        this.hasEnteredViewport = false;
      }

      const isInView = containerTop <= 0 && containerTop + containerHeight >= viewportHeight;

      if (isInView) {
        const scrollProgress = Math.abs(containerTop) / (containerHeight - viewportHeight);
        const newIndex = Math.min(Math.floor(scrollProgress * this.totalProfiles), this.totalProfiles - 1);

        if (newIndex !== this.currentIndex) {
          this.setActiveProfile(newIndex);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true, signal });
    handleScroll();
  }

  lockToSection() {
    this.isAutoScrolling = true;

    const containerTop = this.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: containerTop,
      behavior: 'smooth',
    });

    // Wait for scroll animation to complete
    const checkScrollEnd = () => {
      const currentTop = this.getBoundingClientRect().top;
      if (Math.abs(currentTop) < 2) {
        this.isAutoScrolling = false;
        this.isLocked = true;
      } else {
        requestAnimationFrame(checkScrollEnd);
      }
    };

    requestAnimationFrame(checkScrollEnd);
  }

  setActiveProfile(index) {
    if (index < 0 || index >= this.totalProfiles) return;

    this.profiles.forEach((profile, i) => {
      profile.classList.toggle('is-active', i === index);
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;
  }

  goToProfile(index) {
    if (index < 0 || index >= this.totalProfiles) return;

    this.isAutoScrolling = true;

    // Update visual state immediately
    this.setActiveProfile(index);

    // Get container position (absolute, not relative to viewport)
    const containerRect = this.getBoundingClientRect();
    const containerTopAbsolute = containerRect.top + window.scrollY;
    const containerHeight = this.offsetHeight;
    const viewportHeight = window.innerHeight;
    const scrollableHeight = containerHeight - viewportHeight;

    // Calculate target scroll position
    let targetScroll;
    if (this.totalProfiles === 1) {
      targetScroll = containerTopAbsolute;
    } else {
      const progress = index / (this.totalProfiles - 1);
      targetScroll = containerTopAbsolute + progress * scrollableHeight;
    }

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });

    // Wait for scroll animation to complete
    setTimeout(() => {
      this.isAutoScrolling = false;
    }, 600);
  }
}

customElements.define('scent-profiles-scroller', ScentProfilesScroller);
